import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

const errorText = (e, fallback) => (typeof e.response?.data === 'string' && e.response.data ? e.response.data : fallback);

/** System Admin > Media: where product photos are kept (the database or the media bucket) and a button to move the old ones. */
function MediaStorageAdmin() {
  const [status, setStatus] = useState(null);
  const [moving, setMoving] = useState(false);
  const [log, setLog] = useState('');

  const load = useCallback(() => {
    axios.get('/api/system-admin/media').then((r) => setStatus(r.data)).catch((e) => toast.error(errorText(e, 'Could not load media storage')));
  }, []);
  useEffect(() => { load(); }, [load]);

  // A call moves a few photos at a time; this keeps pressing until none are left, so one click does the lot
  const moveAll = async () => {
    setMoving(true);
    let total = 0;
    try {
      for (let i = 0; i < 1000; i += 1) {
        const { data } = await axios.post('/api/system-admin/media/move-photos?limit=50');
        total += data.moved;
        setStatus(data);
        setLog(`Moved ${total} photo${total === 1 ? '' : 's'}...`);
        if (data.moved === 0 || data.photosInDatabase === 0) break;
      }
      setLog(`Done: moved ${total} photo${total === 1 ? '' : 's'}.`);
      toast.success(`Moved ${total} photo${total === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(errorText(e, 'Stopped'));
      setLog(`Stopped after ${total} photo${total === 1 ? '' : 's'}. Press again to carry on.`);
    }
    setMoving(false);
    load();
  };

  if (!status) return null;
  const s3 = status.storage === 's3';
  return (
    <div data-testid="media-admin">
      <Card className="mb-3">
        <Card.Header><strong>Where product photos are kept</strong></Card.Header>
        <Card.Body>
          <div className="mb-2">
            Storage: {s3 ? <Badge bg="success">S3 bucket (served from the CDN)</Badge> : <Badge bg="secondary">Database</Badge>}
          </div>
          <div className="small text-muted mb-3">
            In the database: <strong data-testid="photos-in-database">{status.photosInDatabase}</strong> photos. In the bucket: <strong data-testid="photos-in-bucket">{status.photosInBucket}</strong>.
          </div>
          {!s3 && (
            <Alert variant="info" className="small">
              Photos are kept in the database. To use the bucket, set <code>MEDIA_STORAGE=s3</code>, <code>MEDIA_S3_BUCKET</code>,
              <code> MEDIA_S3_REGION</code>, <code>MEDIA_PUBLIC_BASE_URL</code> and the AWS access key in the server&apos;s environment, then restart.
            </Alert>
          )}
          <Button onClick={moveAll} disabled={!s3 || moving || status.photosInDatabase === 0} data-testid="move-photos">
            {moving ? 'Moving...' : 'Move photos from the database to the bucket'}
          </Button>
          {log && <span className="small text-muted ms-3">{log}</span>}
          <div className="small text-muted mt-3">
            Each photo is copied first and removed from the database only after its files are stored, so stopping half-way loses nothing: press again to carry on.
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}

export default MediaStorageAdmin;
